import { METRICS_EVENTS } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { increaseAutomaticStreakQueuePriorityV2 } from "@/utils/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

export interface UseIncreaseQueuePriorityOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Top-up: spends priority tickets on the user's active queue item, buying more
 * prioritized epochs while already IN the queue.
 */
export const useIncreaseQueuePriority = (options?: UseIncreaseQueuePriorityOptions) => {
  const { auth } = useHubUser();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: async (request: { campaignName: string; ticketsToSpend: number }) => {
      const result = await increaseAutomaticStreakQueuePriorityV2(auth, request.campaignName, request.ticketsToSpend);

      if (isServerApiRfcError(result)) {
        throw new Error(result.detail);
      }
      if (isServerApiError(result)) {
        throw new Error(result.message);
      }

      return result;
    },
    onSuccess: (_data, variables) => {
      trackEvent(METRICS_EVENTS.AUTOMATIC_STREAK_CREATED, {
        campaign_name: variables.campaignName,
        version: "V2",
        priority_topup: true,
        priority_tickets_spent: variables.ticketsToSpend,
      });

      // Same key prefix as the read hook `[AUTOMATIC_STREAK, "queue", "item"]`.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, "queue", "item"] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAK, "priority-tickets"] });

      enqueueSnackbar(
        `Priority boosted: ${variables.ticketsToSpend} ticket${variables.ticketsToSpend > 1 ? "s" : ""} spent.`,
        { variant: "success" },
      );

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to boost queue priority: ${error.message}`, {
        variant: "error",
      });

      options?.onError?.(error.message);
    },
  });

  return {
    increaseQueuePriority: mutation.mutateAsync,
    isIncreasing: mutation.isPending,
    error: mutation.error?.message,
  };
};
